import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * The shopping list.
 *
 * The honesty rule from the service survives to the screen: a total that could
 * only be built from some of the lines is never shown as if it were the bill.
 * The count of priced lines sits next to it, and the note says what is missing.
 *
 * Prices are typed by whoever saw the shelf. There is no price feed for South
 * African retail that is both legal to use and current, and a stale scraped
 * price presented confidently is worse than a blank.
 */

interface Line {
  foodId: string; name: string; brand: string | null; aisle: string;
  neededG: number; packs: number | null; packG: number | null;
  surplusG: number | null; priceZar: number | null; priceRetailer: string | null;
}

interface Leftover {
  id: string; name: string; gramsRemaining: number; cookedOn: string; recipeId: string | null;
}

interface ListResponse {
  byAisle: Array<{ aisle: string; label: string; lines: Line[] }>;
  lineCount: number;
  pricedLines: number;
  knownTotalZar: number;
  priceNote: string;
  leftovers: Leftover[];
  batches?: Array<{ cookOn: number; covers: number[]; recipes: Array<{ name: string; portions: number }> }>;
}

const RETAILERS = ['Checkers', 'Pick n Pay', 'Spar', 'Woolworths', 'Makro', 'Food Lovers', 'Dis-Chem'] as const;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const amount = (line: Line): string => {
  if (line.packs != null && line.packG != null) {
    return `${line.packs} × ${line.packG >= 1000 ? `${line.packG / 1000} kg` : `${line.packG} g`}`;
  }
  return line.neededG >= 1000 ? `${(line.neededG / 1000).toFixed(2)} kg` : `${Math.round(line.neededG)} g`;
};

export function Shopping(): JSX.Element {
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [pricing, setPricing] = useState<Line | null>(null);
  const [price, setPrice] = useState({ priceZar: '', packG: '', retailer: RETAILERS[0] as string });

  const load = useCallback(async () => {
    try {
      setData(await api<ListResponse>('/shopping'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build a list.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const tick = (id: string): void => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const savePrice = async (): Promise<void> => {
    if (!pricing) return;
    try {
      await api<unknown>('/shopping/price', {
        method: 'POST',
        body: JSON.stringify({
          foodId: pricing.foodId,
          priceZar: Number(price.priceZar),
          packG: Number(price.packG),
          retailer: price.retailer,
        }),
      });
      setPricing(null);
      setPrice({ priceZar: '', packG: '', retailer: RETAILERS[0] });
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That price did not save.');
    }
  };

  const eatLeftover = async (id: string, grams: number): Promise<void> => {
    await api<unknown>(`/shopping/leftovers/${id}/eat`, {
      method: 'POST', body: JSON.stringify({ grams }),
    }).catch(() => undefined);
    void load();
  };

  if (error && !data) return <div className="card"><p className="bad">{error}</p></div>;
  if (!data) return <div className="card"><p className="muted small">Building the list.</p></div>;

  if (data.lineCount === 0) {
    return (
      <div className="card">
        <h2>Nothing to buy</h2>
        <p className="muted small">
          There is no active meal plan, so there is nothing to shop for. Build a week on the Plan
          tab and the list appears here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <h2>This week</h2>
        <p className="big">
          {data.knownTotalZar > 0 ? `R${data.knownTotalZar.toFixed(2)}` : 'No prices yet'}
        </p>
        <p className="muted small">{data.priceNote}</p>
        <p className="muted small">
          {ticked.size} of {data.lineCount} in the trolley.
        </p>
      </div>

      {data.byAisle.map((aisle) => (
        <div className="card" key={aisle.aisle}>
          <h2>{aisle.label}</h2>
          {aisle.lines.map((line) => (
            <div key={line.foodId} className="row between">
              <label className="row">
                <input type="checkbox" checked={ticked.has(line.foodId)}
                       onChange={() => tick(line.foodId)} />
                <span className={ticked.has(line.foodId) ? 'muted' : ''}>
                  {line.name}
                  {line.brand ? <span className="muted small"> {line.brand}</span> : null}
                  <br />
                  <span className="muted small">
                    {amount(line)}
                    {line.surplusG != null && line.surplusG > 0
                      ? ` — ${Math.round(line.surplusG)} g left over`
                      : ''}
                  </span>
                </span>
              </label>
              <span>
                {line.priceZar != null ? (
                  <span className="muted small">
                    R{line.priceZar.toFixed(2)}
                    {line.priceRetailer ? ` at ${line.priceRetailer}` : ''}
                  </span>
                ) : (
                  <button type="button" className="ghost"
                          onClick={() => {
                            setPricing(line);
                            setPrice({
                              priceZar: '',
                              packG: line.packG ? String(line.packG) : '',
                              retailer: RETAILERS[0],
                            });
                          }}>
                    Add price
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      ))}

      {pricing && (
        <div className="card">
          <h2>Price for {pricing.name}</h2>
          <div className="row wrap">
            <label className="field">
              <span>Price (R)</span>
              <input type="number" step="0.01" value={price.priceZar}
                     onChange={(e) => setPrice((p) => ({ ...p, priceZar: e.target.value }))} />
            </label>
            <label className="field">
              <span>Pack size (g)</span>
              <input type="number" value={price.packG}
                     onChange={(e) => setPrice((p) => ({ ...p, packG: e.target.value }))} />
            </label>
            <label className="field">
              <span>Where</span>
              <select value={price.retailer}
                      onChange={(e) => setPrice((p) => ({ ...p, retailer: e.target.value }))}>
                {RETAILERS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          </div>
          <div className="row">
            <button type="button" onClick={() => void savePrice()}>Save</button>
            <button type="button" className="ghost" onClick={() => setPricing(null)}>Cancel</button>
          </div>
          <p className="muted small">
            The date is recorded with it, so a price that has gone stale can say so instead of
            quietly staying wrong.
          </p>
        </div>
      )}

      {data.batches && data.batches.length > 0 && (
        <div className="card">
          <h2>Cooking</h2>
          {data.batches.map((b) => (
            <div key={b.cookOn} className="stack">
              <strong>{DAYS[b.cookOn] ?? `Day ${b.cookOn + 1}`}</strong>
              <span className="muted small">
                covers {b.covers.map((d) => DAYS[d]?.slice(0, 3) ?? d).join(', ')}
              </span>
              {b.recipes.map((r) => (
                <span key={r.name}>{r.name} — {r.portions} portion{r.portions === 1 ? '' : 's'}</span>
              ))}
            </div>
          ))}
        </div>
      )}

      {data.leftovers.length > 0 && (
        <div className="card">
          <h2>Already in the fridge</h2>
          <p className="muted small">
            Cooking 1.4 kg of mince and eating 200 g of it is the normal case, so what is left is
            tracked rather than assumed eaten.
          </p>
          {data.leftovers.map((l) => (
            <div key={l.id} className="row between">
              <span>
                {l.name}
                <br />
                <span className="muted small">
                  {Math.round(l.gramsRemaining)} g, cooked {new Date(l.cookedOn).toLocaleDateString()}
                </span>
              </span>
              <button type="button" className="ghost"
                      onClick={() => void eatLeftover(l.id, l.gramsRemaining)}>
                Finished it
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="bad">{error}</p>}
    </>
  );
}
